import React, { useMemo, useState } from "react";
import {
  Box, Breadcrumbs, Button, Card, FormControl, Grid, InputAdornment,
  Link as MLink, MenuItem, Menu, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import StairsOutlinedIcon from "@mui/icons-material/StairsOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
// Batch 6B — Archive/Restore machinery kept wired at the hook level
// (useArchive/useRestore mutations still imported and bound below) but
// no longer surfaced in the row kebab per §4.2. Reported.
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import TuneIcon from "@mui/icons-material/Tune";
import { CiSearch } from "react-icons/ci";
import { Link, useNavigate } from "react-router-dom";
import {
  useListPromptTemplatesQuery, useDuplicatePromptTemplateMutation,
  useSetDefaultPromptTemplateMutation, useArchivePromptTemplateMutation,
  useRestorePromptTemplateMutation, useDeletePromptTemplateMutation,
} from "../../../../Api/attoDeskApi";
import { useNotifier } from "../../../../Core/Notifier";
import useDebounce from "../../../../Components/SearchDebonce/useDebounce";
import DeletePopup from "../../../../Components/Delete/DeletePopup";
import { LegacyTokens, TypeScale, PageSurface, B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";
import {
  AbaFilterSelect, AbaActionCell, AbaRowIconButton, AbaStatusCell, AbaUpdatedCell, formatAbaDate,
  ABA_TABLE_STATUS_WIDTH, ABA_TABLE_UPDATED_WIDTH, ABA_TABLE_ACTION_WIDTH, ABA_TABLE_DEFAULT_WIDTH,
} from "../../../ProgramLibrary/shared/abaTable";
import TemplateRowKebab from "./TemplateRowKebab";

/**
 * Prompt Levels workspace list.
 *
 * Batch 40 — visual redesign only. Every RTK-Query hook, mutation,
 * filter state, pagination, row action, delete-dialog wiring and
 * navigation route from the prior implementation is preserved verbatim.
 */
export default function PromptHierarchiesList() {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const debounced = useDebounce(search, 300);

  const { data, isFetching, refetch } = useListPromptTemplatesQuery({
    // Batch 6B follow-up (user §12.2 option a) — send an explicit
    // sort key so the ↑ ascending caret on the "Template name" header
    // matches the actual server-side order. Backend accepts name_asc,
    // name_desc, updated_asc, updated_desc; anything else falls back
    // to name_asc.
    searchText: debounced, status: status || null, page: page + 1, perPage, sort: "name_asc",
  });
  const rows: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];
  const total: number = Number((data as any)?.total ?? 0);
  const lastPage = Math.max(1, Math.ceil(total / perPage));

  const [duplicate] = useDuplicatePromptTemplateMutation();
  const [setDefault] = useSetDefaultPromptTemplateMutation();
  const [archive] = useArchivePromptTemplateMutation();
  const [restore] = useRestorePromptTemplateMutation();
  const [remove] = useDeletePromptTemplateMutation();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const [deleteRow, setDeleteRow] = useState<any>(null);

  const call = async (fn: () => Promise<any>, ok: string) => {
    try {
      const r = await fn();
      if (r?.status) { showMessage(r.message || ok); refetch(); }
      else showErrorMessage(r?.message || "Action failed");
    } catch { showErrorMessage("Action failed"); }
  };

  // Summary strip — from real data only. Programs/Targets sums are per-page
  // (list endpoint returns per-row usage, not global totals) and are
  // captioned as such rather than pretending to be library-wide numbers.
  const summary = useMemo(() => {
    const active = rows.filter((r) => (r.status || "active") === "active").length;
    const hasDefault = rows.some((r) => !!r.isDefault);
    const programsSum = rows.reduce((s, r) => s + (r.programsUsing ?? 0), 0);
    const targetsSum  = rows.reduce((s, r) => s + (r.targetsUsing ?? 0), 0);
    const levelsSum   = rows.reduce((s, r) => s + (r.levelCount ?? 0), 0);
    return { total, active, hasDefault, programsSum, targetsSum, levelsSum };
  }, [rows, total]);
  const showSummary = !isFetching && total > 0;
  const anyFilter = !!(search || status);

  return (
    <Box sx={{ ...PageSurface, p: { xs: 2, md: 3.5 } }}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1.5, fontSize: 12 }} separator="/">
          <MLink underline="hover" component={Link} to="/aba-therapy/settings/prompt-levels" sx={{ color: B13Ink.ink3 }}>Settings</MLink>
          <Typography sx={{ fontSize: 12, color: LegacyTokens.ink }}>Prompt Level Templates</Typography>
        </Breadcrumbs>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { md: "flex-start" }, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
              backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <StairsOutlinedIcon fontSize="medium" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography component="h1" sx={{ ...TypeScale.pageTitle, lineHeight: 1.15 }}>
                  Prompt Level Templates
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
                  fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  px: 1, py: 0.25, borderRadius: `${LegacyTokens.radius.chip}px`,
                }}>Clinical Prompting System</Box>
              </Box>
              {/* Batch 6B §6B.1 — description paragraph reproduced VERBATIM
                 from the client's page, including "The allows". This is
                 the client's copy on the client's screen; do not silently
                 correct. (Kerthiga has been asked whether she wants
                 "This allows" instead — one-line change if yes.) */}
              <Typography sx={{ ...TypeScale.caption, mt: 0.5, maxWidth: 780 }}>
                Manage the levels of prompting you use when recording data in your sessions. The allows you to get a better insight into what prompt guidance is being used and how prompts are faded. You can set up multiple lists of levels of prompting for use with different types of programs.
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
            <Button
              size="medium"
              startIcon={<AutoAwesomeIcon fontSize="small" />}
              onClick={() => navigate("/aba-therapy/settings/prompt-levels/new?preset=standard")}
              sx={{
                backgroundColor: LegacyTokens.surface, color: B13.primary,
                border: `1px solid ${tint12(B13.primary)}`,
                fontWeight: 600, textTransform: "none",
                px: 2, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
                "&:hover": { backgroundColor: tint12(B13.primary), borderColor: B13.primary },
              }}
            >
              Use standard template
            </Button>
            <Button
              variant="contained" size="medium"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => navigate("/aba-therapy/settings/prompt-levels/new")}
              sx={{
                backgroundColor: "#1A73E8", color: LegacyTokens.surface,
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                px: 2.5, py: 1, borderRadius: `${LegacyTokens.radius.control}px`,
                boxShadow: "0 1px 2px rgba(26, 115, 232, 0.20)",
                "&:hover": { backgroundColor: "#1662C4", boxShadow: "0 2px 6px rgba(26, 115, 232, 0.30)" },
              }}
            >
              New Prompt Level Template
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* ── Summary strip (from real data only) ────────────────────── */}
      {showSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }} data-testid="hierarchies-summary-strip">
          <SummaryCard label="Total Templates" value={total} icon={<StairsOutlinedIcon fontSize="small" />} accent={LegacyTokens.purple} tile={LegacyTokens.purpleSoft} />
          <SummaryCard label="Active on page" value={summary.active} icon={<CheckCircleOutlineIcon fontSize="small" />} accent={LegacyTokens.teal} tile={LegacyTokens.tealSoft} />
          <SummaryCard label="Default set" value={summary.hasDefault ? "Yes" : "—"} icon={<StarIcon fontSize="small" />} accent={LegacyTokens.emerald} tile={LegacyTokens.emeraldSoft} />
          <SummaryCard label="Programs Using" value={summary.programsSum} icon={<PeopleAltOutlinedIcon fontSize="small" />} accent={LegacyTokens.blue} tile={LegacyTokens.blueSoft} pageScoped />
          <SummaryCard label="Targets Using" value={summary.targetsSum} icon={<PlaylistAddCheckIcon fontSize="small" />} accent={LegacyTokens.amber} tile={LegacyTokens.amberSoft} pageScoped />
          <SummaryCard label="Total Levels" value={summary.levelsSum} icon={<LayersOutlinedIcon fontSize="small" />} accent={B13.primary} tile={tint12(B13.primary)} pageScoped />
        </Grid>
      )}

      {/* ── Search + filter toolbar ────────────────────────────────── */}
      <Card variant="outlined" sx={{
        p: 2, mb: 2,
        backgroundColor: LegacyTokens.surface,
        borderColor: LegacyTokens.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
        boxShadow: "none",
      }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={anyFilter ? 7 : 9}>
            <TextField size="small" fullWidth label="Search" placeholder="Search Prompt Levels" value={search}
              onChange={(e) => { setPage(0); setSearch(e.target.value); }}
              sx={{
                "& .MuiInputLabel-root": { fontSize: 13 },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: LegacyTokens.surface, height: 44,
                  borderRadius: `${LegacyTokens.radius.control}px`,
                  "& fieldset": { borderColor: LegacyTokens.line },
                  "&:hover fieldset": { borderColor: B13.primary },
                  "&.Mui-focused fieldset": { borderColor: B13.primary },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: B13Ink.ink3 }} /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AbaFilterSelect
              label="Status"
              value={status}
              emptyLabel="All statuses"
              onChange={(v) => { setPage(0); setStatus(v); }}
              fullWidth
              options={[
                { value: "draft", label: "Draft" },
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
            />
          </Grid>
          {anyFilter && (
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                onClick={() => { setSearch(""); setStatus(""); setPage(0); }}
                sx={{
                  color: B13.primary, textTransform: "none", fontWeight: 600,
                  border: `1px solid ${tint12(B13.primary)}`,
                  height: 44, borderRadius: `${LegacyTokens.radius.control}px`,
                  "&:hover": { backgroundColor: tint12(B13.primary) },
                }}>Clear filters</Button>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* ── Table card ──────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{
        overflow: "hidden",
        backgroundColor: LegacyTokens.surface,
        borderColor: LegacyTokens.line,
        borderRadius: `${LegacyTokens.radius.card}px`,
        boxShadow: "none",
      }}>
        <Box sx={{ overflowX: "auto" }}>
          {/* Batch 6B §6B.2 — table pared to exactly two columns per the
             reference (Template name, Default for new programs?) plus the
             actions column. Columns removed from view: Prompting Order,
             Levels count, Programs/Targets, Status, Updated. Those data
             are still visible in the summary strip above and in the
             editor. Row action column kept per §6B.2 anatomy (pencil
             then kebab). Table shape intentionally isolated — when
             SettingsDataTable (Batch 5B) eventually ships, this markup
             lifts out and the row+column defs feed into the shared
             component. */}
          <Table size="small" sx={{
            minWidth: 720,
            tableLayout: "fixed",
            "& td, & th": { borderBottomColor: LegacyTokens.line, py: 1.5, px: 2 },
          }}>
            <colgroup>
              <col />
              <col style={{ width: 220 }} />
              <col style={{ width: ABA_TABLE_ACTION_WIDTH }} />
            </colgroup>
            <TableHead>
              <TableRow sx={{ backgroundColor: B13.canvas, "& th": { height: 48 } }}>
                <TableCell sx={{ ...TypeScale.tableHeader }}>
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    Template name
                    <ArrowUpwardIcon sx={{ fontSize: 14, color: B13Ink.ink3 }} />
                  </Box>
                </TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Default for new programs?</TableCell>
                <TableCell sx={{ ...TypeScale.tableHeader }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 3 }).map((__, j) => (
                      <TableCell key={j} sx={{ py: 2.25 }}>
                        <Box sx={{ height: 14, borderRadius: 0.5, backgroundColor: B13.canvas, opacity: 0.6 }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      display: "inline-flex", width: 48, height: 48, borderRadius: `${LegacyTokens.radius.control}px`,
                      backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
                      alignItems: "center", justifyContent: "center", mb: 2,
                    }}>
                      <StairsOutlinedIcon />
                    </Box>
                    <Typography sx={{ ...TypeScale.sectionHead, mb: 0.5 }}>
                      {anyFilter ? "No Prompt Levels match your filters." : "Create your first Prompt Levels"}
                    </Typography>
                    <Typography sx={{ ...TypeScale.caption, maxWidth: 480, mx: "auto", mb: 2.5 }}>
                      {anyFilter
                        ? "Try clearing the search or status filter, or create a new template."
                        : "Define the levels of assistance Staff can select while recording ABA Trials."}
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                      {anyFilter ? (
                        <Button size="small"
                          onClick={() => { setSearch(""); setStatus(""); setPage(0); }}
                          sx={{
                            color: B13.primary, textTransform: "none", fontWeight: 600,
                            border: `1px solid ${tint12(B13.primary)}`,
                            "&:hover": { backgroundColor: tint12(B13.primary) },
                          }}>Clear filters</Button>
                      ) : (
                        <Button size="small" startIcon={<AutoAwesomeIcon fontSize="small" />}
                          onClick={() => navigate("/aba-therapy/settings/prompt-levels/new?preset=standard")}
                          sx={{
                            color: B13.primary, textTransform: "none", fontWeight: 600,
                            border: `1px solid ${tint12(B13.primary)}`,
                            "&:hover": { backgroundColor: tint12(B13.primary) },
                          }}>Use standard template</Button>
                      )}
                      <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />}
                        onClick={() => navigate("/aba-therapy/settings/prompt-levels/new")}
                        sx={{
                          backgroundColor: B13.primary, color: LegacyTokens.surface,
                          textTransform: "none", fontWeight: 600,
                          "&:hover": { backgroundColor: B13.primary },
                        }}>New Prompt Level Template</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover sx={{
                  "&:hover": { backgroundColor: LegacyTokens.canvas },
                }}>
                  <TableCell sx={{ py: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {/* Batch 6B §6B.3 — padlock indicator when locked.
                         DORMANT until backend returns isLocked (schema
                         column ships in the follow-up batch); reads
                         `row.isLocked` which is undefined today and
                         renders nothing. When the column lands, no FE
                         change required. */}
                      {r.isLocked && (
                        <Tooltip title="Locked" arrow>
                          <LockOutlinedIcon
                            fontSize="small"
                            sx={{ color: B13Ink.ink3, flexShrink: 0 }}
                            aria-label="Locked template"
                          />
                        </Tooltip>
                      )}
                      <MLink component={Link} to={`/aba-therapy/settings/prompt-levels/${r.id}`} underline="hover" sx={{ color: LegacyTokens.ink }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: LegacyTokens.ink }}>{r.templateName}</Typography>
                      </MLink>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    {/* §4.1 (user decision) — checkmark when true, empty
                       when false. Mutual exclusion enforced server-side
                       in PromptLevelTemplateController::setDefault(). */}
                    {r.isDefault ? (
                      <CheckIcon sx={{ color: B13.primary, fontSize: 22 }} aria-label="Default template" />
                    ) : (
                      <Box component="span" aria-hidden />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ px: 1.5 }}>
                    <AbaActionCell>
                      <AbaRowIconButton title="Edit template" onClick={() => navigate(`/aba-therapy/settings/prompt-levels/${r.id}/edit`)}>
                        <EditIcon fontSize="small" />
                      </AbaRowIconButton>
                      <AbaRowIconButton title="More actions" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(r); }}>
                        <MoreVertIcon fontSize="small" />
                      </AbaRowIconButton>
                    </AbaActionCell>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* ── Pagination footer ────────────────────────────────────── */}
        <Box sx={{
          borderTop: `1px solid ${LegacyTokens.line}`,
          backgroundColor: B13.canvas,
          p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 1,
        }}>
          <Typography sx={{ ...TypeScale.caption }}>
            {total === 0
              ? "0 Prompt Levels"
              : (() => {
                  const from = Math.min(total, page * perPage + 1);
                  const to = Math.min(total, (page + 1) * perPage);
                  return `Showing ${from}–${to} of ${total} Prompt Level Template${total === 1 ? "" : "s"}`;
                })()}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ ...TypeScale.caption }}>Rows</Typography>
            <FormControl size="small">
              <Select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
                sx={{
                  minWidth: 80, height: 36,
                  backgroundColor: LegacyTokens.surface,
                  borderRadius: `${LegacyTokens.radius.control}px`,
                  "& fieldset": { borderColor: LegacyTokens.line },
                }}>
                {[15, 25, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="small" disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{
                color: B13.primary, textTransform: "none", fontWeight: 600,
                border: `1px solid ${tint12(B13.primary)}`,
                backgroundColor: LegacyTokens.surface,
                "&:hover": { backgroundColor: tint12(B13.primary) },
                "&.Mui-disabled": { color: B13Ink.ink3, borderColor: LegacyTokens.line },
              }}>Prev</Button>
            <Button size="small" disabled={page + 1 >= lastPage || total === 0}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                color: B13.primary, textTransform: "none", fontWeight: 600,
                border: `1px solid ${tint12(B13.primary)}`,
                backgroundColor: LegacyTokens.surface,
                "&:hover": { backgroundColor: tint12(B13.primary) },
                "&.Mui-disabled": { color: B13Ink.ink3, borderColor: LegacyTokens.line },
              }}>Next</Button>
          </Stack>
        </Box>
      </Card>

      {/* Batch 6B follow-up — kebab now 5 items (user Option 2 on §10):
         Duplicate, Set as default, Archive/Restore, Delete. Lock/Unlock
         removed pending the isLocked schema batch; dev-facing "coming
         soon" toast broke the same rule as "Rating Scale UI pending
         client reference" (no dev copy in front of the client). */}
      <TemplateRowKebab
        anchor={menuAnchor}
        row={menuRow}
        onClose={() => setMenuAnchor(null)}
        onDuplicate={() => call(() => duplicate(menuRow.id).unwrap(), "Duplicated")}
        onSetDefault={() => call(() => setDefault(menuRow.id).unwrap(), "Default updated")}
        onArchive={() => call(() => archive(menuRow.id).unwrap(), "Archived")}
        onRestore={() => call(() => restore(menuRow.id).unwrap(), "Restored")}
        onDelete={() => setDeleteRow(menuRow)}
      />

      {deleteRow && (
        <DeletePopup
          open
          handleCloseDelete={() => setDeleteRow(null)}
          onConfirm={async () => {
            await call(() => remove(deleteRow.id).unwrap(), "Deleted");
            setDeleteRow(null);
          }}
          title="Delete Prompt Levels"
          content={`Delete "${deleteRow.templateName}"? This is blocked if the template is in use.`}
        />
      )}
    </Box>
  );
}

// ─── Local components (visual only) ─────────────────────────────────

const SummaryCard: React.FC<{
  label: string; value: React.ReactNode; icon: React.ReactNode;
  accent: string; tile: string; pageScoped?: boolean;
}> = ({ label, value, icon, accent, tile, pageScoped }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Box sx={{
      height: "100%", p: 2,
      display: "flex", flexDirection: "column", gap: 1.25,
      backgroundColor: LegacyTokens.surface,
      border: `1px solid ${LegacyTokens.line}`,
      borderRadius: `${LegacyTokens.radius.card}px`,
      transition: "box-shadow 150ms",
      "&:hover": { boxShadow: "0 2px 8px rgba(15, 61, 46, 0.06)" },
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: `${LegacyTokens.radius.control}px`,
        backgroundColor: tile, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: 24, fontWeight: 650, color: LegacyTokens.ink, lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ ...TypeScale.fieldLabel, display: "block", mt: 0.5 }}>
          {label}
          {pageScoped && <Box component="span" sx={{ ml: 0.5, textTransform: "none", letterSpacing: 0, fontSize: 10, fontWeight: 500, color: B13Ink.ink3 }}>(on page)</Box>}
        </Typography>
      </Box>
    </Box>
  </Grid>
);

// Batch 40 §11 — compact prompt ladder inside the Hierarchy cell.
// Renders up to five level chips with a "+N more" pill when longer.
// Each chip uses its stored `colour` when present, otherwise falls back
// to a name-based palette. Tooltip on each chip surfaces the full level
// name + score. Accessible aria-label on the wrapper carries the plain
// text order so screen readers get the same information.
const LADDER_FALLBACK: Record<string, string> = {
  "full physical":    LegacyTokens.red,
  "partial physical": LegacyTokens.clay,
  "model":            LegacyTokens.amber,
  "gestural":         LegacyTokens.teal,
  "verbal":           LegacyTokens.blue,
  "visual":           LegacyTokens.purple,
  "independent":      LegacyTokens.emerald,
};
const PromptLadder: React.FC<{ levels?: any[]; direction?: string }> = ({ levels, direction }) => {
  const list = Array.isArray(levels)
    ? [...levels]
        .filter((l) => (l?.isActive ?? true) !== false)
        .sort((a, b) => (a?.orderNumber ?? 0) - (b?.orderNumber ?? 0))
    : [];
  if (list.length === 0) return null;
  const dir = String(direction ?? "").toLowerCase();
  const ordered = dir === "least_to_most" ? list.slice().reverse() : list;
  const names = ordered.map((l) => String(l?.levelName ?? ""));
  const maxChips = 5;
  const shown = ordered.slice(0, maxChips);
  const hidden = ordered.slice(maxChips);
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}
      aria-label={`Prompt levels: ${names.join(" → ")}`}>
      {shown.map((lvl, i) => {
        const name = String(lvl?.levelName ?? "");
        const colour = String(lvl?.colour ?? "") || LADDER_FALLBACK[name.toLowerCase()] || B13Ink.ink3;
        const short = String(lvl?.shortCode ?? "").trim() || name;
        return (
          <React.Fragment key={i}>
            <Tooltip title={`${name}${lvl?.score != null ? ` · ${lvl.score}` : ""}`} arrow>
              <Box component="span" sx={{
                display: "inline-flex", alignItems: "center", gap: 0.5,
                backgroundColor: hexToTint(colour), color: darken(colour),
                borderRadius: `${LegacyTokens.radius.chip}px`,
                fontSize: 11, fontWeight: 600, lineHeight: 1.2,
                px: 0.75, py: 0.25,
              }}>
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: colour }} />
                {short}
              </Box>
            </Tooltip>
            {i < shown.length - 1 && (
              <Box component="span" sx={{ mx: 0.5, color: B13Ink.ink3, fontSize: 12 }} aria-hidden>→</Box>
            )}
          </React.Fragment>
        );
      })}
      {hidden.length > 0 && (
        <>
          <Box component="span" sx={{ mx: 0.5, color: B13Ink.ink3, fontSize: 12 }} aria-hidden>→</Box>
          <Tooltip title={hidden.map((l) => l?.levelName).filter(Boolean).join(" → ")} arrow>
            <Box component="span" sx={{
              backgroundColor: B13.canvas, color: B13Ink.ink3,
              borderRadius: `${LegacyTokens.radius.chip}px`,
              fontSize: 11, fontWeight: 600,
              px: 0.75, py: 0.25,
            }}>+{hidden.length} more</Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

// Turn a hex colour into a 12% tint on a white surface (soft chip bg).
function hexToTint(hex: string): string {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  if (Number.isNaN(bigint)) return B13.canvas;
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  // 12% opacity mixed with white
  const mix = (c: number) => Math.round(c * 0.15 + 255 * 0.85);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
}
function darken(hex: string): string {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  if (Number.isNaN(bigint)) return LegacyTokens.ink;
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  const d = (c: number) => Math.max(0, Math.round(c * 0.55));
  return `rgb(${d(r)}, ${d(g)}, ${d(b)})`;
}

const DirectionBadge: React.FC<{ direction?: string }> = ({ direction }) => {
  const raw = String(direction ?? "most_to_least").toLowerCase();
  const map: Record<string, { label: string; bg: string; text: string; dot: string; icon: React.ReactNode }> = {
    most_to_least:  { label: "Most-to-Least",  bg: LegacyTokens.purpleSoft, text: LegacyTokens.purpleText, dot: LegacyTokens.purple, icon: <KeyboardDoubleArrowDownIcon sx={{ fontSize: 14 }} /> },
    least_to_most:  { label: "Least-to-Most",  bg: LegacyTokens.tealSoft,   text: LegacyTokens.tealText,   dot: LegacyTokens.teal,   icon: <KeyboardDoubleArrowUpIcon sx={{ fontSize: 14 }} /> },
    custom:         { label: "Custom Order",   bg: LegacyTokens.blueSoft,   text: LegacyTokens.blueText,   dot: LegacyTokens.blue,   icon: <TuneIcon sx={{ fontSize: 14 }} /> },
  };
  const t = map[raw] ?? map.most_to_least;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      backgroundColor: t.bg, color: t.text,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600,
      px: 1, py: 0.25,
    }}>
      {t.icon}
      {t.label}
    </Box>
  );
};

const LevelCountBadge: React.FC<{ count: number }> = ({ count }) => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center", gap: 0.5,
    backgroundColor: LegacyTokens.purpleSoft, color: LegacyTokens.purple,
    borderRadius: `${LegacyTokens.radius.chip}px`,
    fontSize: 12, fontWeight: 600,
    px: 1, py: 0.25,
  }}>
    <LayersOutlinedIcon sx={{ fontSize: 14 }} />
    {count} {count === 1 ? "Level" : "Levels"}
  </Box>
);

const UsageCell: React.FC<{ programs: number; targets: number }> = ({ programs, targets }) => (
  <Box>
    <Typography sx={{ fontSize: 13, color: LegacyTokens.ink, fontWeight: 500 }}>
      {programs} Program{programs === 1 ? "" : "s"}
    </Typography>
    <Typography sx={{ fontSize: 12, color: B13Ink.ink3 }}>
      {targets} Target{targets === 1 ? "" : "s"}
    </Typography>
  </Box>
);

const DefaultBadge: React.FC = () => (
  <Box component="span" sx={{
    display: "inline-flex", alignItems: "center", gap: 0.5,
    backgroundColor: LegacyTokens.emeraldSoft, color: LegacyTokens.emeraldText,
    borderRadius: `${LegacyTokens.radius.chip}px`,
    fontSize: 12, fontWeight: 600,
    px: 1, py: 0.25,
  }}>
    <StarIcon sx={{ fontSize: 14, color: LegacyTokens.emerald }} />
    Default
  </Box>
);

const HierarchyStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const raw = String(status ?? "active").toLowerCase();
  const map: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    active:   { bg: LegacyTokens.emeraldSoft, text: LegacyTokens.emeraldText, dot: LegacyTokens.emerald, label: "Active" },
    draft:    { bg: LegacyTokens.amberSoft,   text: LegacyTokens.amberText,   dot: LegacyTokens.amber,   label: "Draft" },
    archived: { bg: LegacyTokens.neutralSoft, text: LegacyTokens.neutralText, dot: LegacyTokens.neutralDot, label: "Archived" },
  };
  const t = map[raw] ?? map.active;
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: 0.75,
      backgroundColor: t.bg, color: t.text,
      borderRadius: `${LegacyTokens.radius.chip}px`,
      fontSize: 12, fontWeight: 600,
      px: 1, py: 0.25,
    }}>
      <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: t.dot }} />
      {t.label}
    </Box>
  );
};

